import { useRef, type FocusEvent } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LogOut, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { PANEL_LINK } from "./navStyles";
import { useDismiss } from "./useDismiss";

/** The signed-in account button on desktop and its panel. Signed-out visitors get a plain "Sign in" link instead. */
export default function AccountMenu({
  email,
  open,
  onOpenChange,
  onSignOut,
}: {
  email: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSignOut: () => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useDismiss(open, wrapRef, (restoreFocus) => {
    onOpenChange(false);
    if (restoreFocus) buttonRef.current?.focus();
  });

  const onBlur = (e: FocusEvent) => {
    const next = e.relatedTarget as Node | null;
    if (open && next && !wrapRef.current?.contains(next)) onOpenChange(false);
  };

  return (
    <div ref={wrapRef} className="relative" onBlur={onBlur}>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Account menu"
        aria-expanded={open}
        aria-controls="nav-panel-account"
        onClick={() => onOpenChange(!open)}
        className={cn(
          "flex h-11 w-11 items-center justify-center rounded-control transition-colors duration-150 hover:bg-secondary hover:text-foreground",
          open ? "bg-secondary text-foreground" : "text-muted-foreground",
        )}
      >
        <User className="h-5 w-5" aria-hidden="true" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            id="nav-panel-account"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.15, ease: "easeOut" } }}
            exit={{ opacity: 0, y: -4, transition: { duration: 0.1, ease: "easeIn" } }}
            className="absolute right-0 top-full mt-2 w-64 rounded-xl border border-border bg-popover p-2 text-popover-foreground shadow-elevated"
          >
            <div className="border-b border-border px-3 pb-3 pt-2">
              <p className="truncate text-sm font-medium text-foreground">{email}</p>
              <p className="text-xs text-muted-foreground">Free account</p>
            </div>
            <ul className="pt-2">
              <li>
                <Link to="/settings" onClick={() => onOpenChange(false)} className={PANEL_LINK}>
                  Account settings
                </Link>
              </li>
              <li>
                <Link to="/stack-builder" onClick={() => onOpenChange(false)} className={PANEL_LINK}>
                  My stacks
                </Link>
              </li>
              <li>
                <button type="button" onClick={onSignOut} className={cn(PANEL_LINK, "w-full text-left")}>
                  <LogOut className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  Sign out
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
